import React, { useEffect, useState, useContext } from 'react';
import IframeResizer from '@iframe-resizer/react';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { useParams, useNavigate } from 'react-router-dom';

import {
  useCourseOutline,
  useProfileCourses,
  useHeaderFooter,
  useCoursePreview,
} from '../../hooks';
import { useNavigateToUnit } from '../../hooks/useMessageBus';
import {
  CourseMap,
  CourseNavigation,
  CourseProgress,
  CourseUnavailable,
  SpinLoader,
} from '../../components';
import {
  extractContents,
  getLmsCourseUnitUrl,
  getFirstUnitId,
  getFirstSequenceId,
  getNextUnitInfo,
  getNextCourseId,
} from '../../utils';
import { ArrowRightIcon } from '../../img/icons';
import { AppDataContext } from '../../context';

import '../../scss/CourseWorking.scss';
import messages from './messages';

import arrowLeftImg from '../../img/arrow-left-blue.svg';
import arrowRightImg from '../../img/arrow-right-blue.svg';
import progressCollapseImg from '../../img/courseWorking/progress_collapse.png';
import progressExpandImg from '../../img/courseWorking/progress_expand.png';

interface CourseWorkingProps {
  children?: React.ReactNode;
}

const CourseWorking: React.FC<CourseWorkingProps> = ({ children }) => {
  const [currentUnitID, setCurrentUnitID] = useState<string>('');
  const [iframeURL, setIframeURL] = useState<string>('');
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [refresher, setRefresher] = useState<number>(0);
  const [formattedCourseName, setFormattedCourseName] = useState<string[]>([]);

  const { headerContent, footerContent } = useHeaderFooter(children);
  const { courseID = '', subsectionId = '', unitID = '' } = useParams<{ courseID: string; subsectionId: string, unitID: string }>();
  const { authenticatedUser, allowedCourses, allowedCoursesLoaded } = useContext(AppDataContext);

  const intl = useIntl();
  const { LMS_BASE_URL } = getConfig();
  const navigate = useNavigate();
  const { courseInfo, courseInfoLoaded, courseInfoErrorStatus } = useCoursePreview(courseID);
  const firstOfAllowedCourseId = allowedCourses[0]?.course_id;
  const isCourseAllowed = allowedCourses.some(({ course_id: id }) => id === courseID);

  const { getCourseCompletions, isUserCoursesCompletionsLoaded } = useProfileCourses(authenticatedUser);

  const {
    courseMetadata,
    courseMetadataLoaded,
    hasFetchedData,
  } = useCourseOutline(courseID, refresher);

  const { course_outline: courseOutline } = courseMetadata;
  const courseOutlineData = courseOutline?.[0] ?? {};

  const handleCourseNameFormatting = () => {
    const courseName = courseMetadata?.display_name || '';
    const separator = '5 Day'; // TODO: refactoring is necessary after implementing localization

    if (String(courseName).includes(separator)) {
      setFormattedCourseName(String(courseName).split(separator));
    }
  };

  useEffect(() => {
    if (courseMetadataLoaded && courseID) {
      handleCourseNameFormatting();
      const blockID = unitID || getFirstUnitId(extractContents(courseOutlineData));
      const sequenceID = subsectionId || getFirstSequenceId(courseOutlineData?.children || []);
      setCurrentUnitID(blockID);
      setIframeLoading(true);
      getLmsCourseUnitUrl(courseID, sequenceID, blockID, (url: string) => {
        setIframeURL(url);
        setIframeLoading(false);
      });
    }
  }, [courseID, subsectionId, unitID, courseMetadataLoaded, courseOutlineData]);

  const handleNavigateToUnit = (parentId: string, unitId: string) => {
    if (courseID) {
      navigate(`/course-working/${courseID}/${parentId}/${unitId}`);
    }
  };

  // Handle postMessage events for navigation
  useNavigateToUnit(({ prereqId }) => {
    const matchingUnit = extractContents(courseOutlineData).find(
      (unit: any) => unit.parentId === prereqId,
    );

    if (matchingUnit) {
      const unitId = getFirstUnitId(extractContents(courseOutlineData));
      handleNavigateToUnit(prereqId, unitId);
    } else {
      console.warn('No matching unit found for prereqId:', prereqId); // eslint-disable-line no-console
    }
  });

  const handleRefreshOutline = () => {
    hasFetchedData.current = false;
    setTimeout(() => {
      setRefresher(Date.now());
    }, 200);
  };

  const handleNextUnit = () => {
    const {
      unitId: nextUnitId,
      parentId: nextParentId,
    } = getNextUnitInfo(extractContents(courseOutlineData), currentUnitID);
    if (nextUnitId !== currentUnitID) {
      handleRefreshOutline();
      handleNavigateToUnit(nextParentId, nextUnitId);
    }
  };

  const handlePreviousUnit = () => {
    const contents = extractContents(courseOutlineData);
    const currentIndex = contents.findIndex(content => content.id === currentUnitID);
    if (currentIndex > 0) {
      handleRefreshOutline();
      const { id: previousUnitId, parentId: previousParentId } = contents[currentIndex - 1];
      handleNavigateToUnit(previousParentId, previousUnitId);
    }
  };

  const isFirstUnit = currentUnitID === getFirstUnitId(extractContents(courseOutlineData));
  const isLastUnit = currentUnitID === getNextUnitInfo(extractContents(courseOutlineData), currentUnitID)?.unitId;

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const handleNextCourse = () => {
    const nextCourseId = getNextCourseId(courseID || '', allowedCourses);
    if (nextCourseId !== courseID) {
      handleRefreshOutline();
      navigate(`/course-overview/${nextCourseId}`);
    }
  };

  const isCourseUnavailable = allowedCoursesLoaded
    && (!isCourseAllowed || (courseInfoLoaded && courseInfoErrorStatus === 404));

  let bodyOverride: React.ReactNode = null;
  if (!allowedCoursesLoaded) {
    bodyOverride = <SpinLoader size={50} containerHeight="40vh" />;
  } else if (isCourseUnavailable) {
    bodyOverride = <CourseUnavailable />;
  }

  return (
    <>
      <input
        type="hidden"
        id="anPageName"
        name="page"
        defaultValue="course-progress"
      />
      <div className="container-center-horizontal">
        <div className="course-progress screen">
          <div className="overlap-group8">
            <div className=" overlap-group6 overlap-group6-custom">
              <div className="hero-image">
                <div className="overlap-group">
                  {courseInfo?.banner_image_asset_path && (
                    <img
                      className="inbal-marilli-lg-ui"
                      src={`${LMS_BASE_URL}${courseInfo?.banner_image_asset_path}`}
                      alt=""
                    />
                  )}
                  <div className="rectangle-20" />
                </div>
              </div>
              <div className="course-headline">
                <h1 className="course-01-why-lead instrumentserif-normal-white-100-1px-2">
                  <span className="instrumentserif-normal-white-100-1px">
                    {intl.formatMessage(messages.module)}
                    <br />
                  </span>
                  {formattedCourseName.length > 0 ? (
                    <span className="recklessneue-regular-normal-white-100-1px c_title">
                      {formattedCourseName[0]}
                      <br />
                      {intl.formatMessage(messages.fiveDayNamePart)}
                      {' '}
                      {formattedCourseName[1]}
                    </span>
                  ) : (
                    <span className="recklessneue-regular-normal-white-100-1px c_title">
                      {courseMetadata.display_name || ''}
                    </span>
                  )}
                </h1>
              </div>
            </div>
            {headerContent}
            {!bodyOverride && (
            <>
            <div // eslint-disable-line
              style={{ height: isExpanded ? 'auto' : '50px' }}
              className="course-progress-container"
              onClick={toggleExpand}
            >
              {isExpanded ? (
                <>
                  <div
                    className="oxford instrumentsans-normal-white-24px"
                    style={{
                      marginTop: '0',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {intl.formatMessage(messages.collapseFullCourseProgress)}
                    <img
                      src={progressCollapseImg}
                      style={{
                        width: '30px',
                        height: '30px',
                        marginLeft: '10px',
                      }}
                      alt=""
                    />
                  </div>
                  <CourseProgress
                    getCourseCompletions={getCourseCompletions}
                    isUserCoursesCompletionsLoaded={isUserCoursesCompletionsLoaded}
                  />
                </>
              ) : (
                <div
                  className="oxford instrumentsans-normal-white-24px"
                  style={{
                    marginTop: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {intl.formatMessage(messages.expandFullCourseProgress)}
                  <img
                    src={progressExpandImg}
                    style={{
                      width: '30px',
                      height: '30px',
                      marginLeft: '10px',
                    }}
                    alt=""
                  />
                </div>
              )}
            </div>
              <div id="page-nav">
                <div className="page-nav">
                  <div className="nav-cont">
                    <div className="progress-nav">
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                      <img
                        className={`arrow-1 arrow nav_btn previous_btn ${isFirstUnit ? 'disabled' : ''}`}
                        src={arrowLeftImg}
                        alt="Arrow Left"
                        onClick={!isFirstUnit ? handlePreviousUnit : undefined}
                      />
                      <p className="chapter-one-what-to-expect recklessneue-book-normal-congress-blue-22-8px c-active" />
                    <img // eslint-disable-line
                      className={`arrow nav_btn next_btn ${isLastUnit ? 'disabled' : ''}`}
                      src={arrowRightImg}
                      alt="Arrow Right"
                      onClick={!isLastUnit ? handleNextUnit : handleNextCourse}
                      style={{ display: (getNextCourseId(courseID || '', allowedCourses) === firstOfAllowedCourseId && isLastUnit) ? 'none' : 'flex' }}
                    />
                    </div>
                    <CourseNavigation courseID={courseID} />
                  </div>
                </div>
              </div>
            </>
            )}
          </div>
          {bodyOverride ?? (
            <div className="working-main" id="working_main">
              {courseMetadataLoaded && (
              <div className="course-map-wrapper">
                <CourseMap
                  courseOutlineData={courseOutlineData}
                  unitID={currentUnitID}
                  courseID={courseID}
                  handleRefreshOutline={handleRefreshOutline}
                />
              </div>
              )}
              <div className="c-iframe">
                {courseMetadataLoaded && !iframeLoading ? (
                  <IframeResizer
                    license="GPLv3"
                    className="w-skeleton unit-iframe"
                    id="edx-iframe"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    sandbox="allow-modals allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-downloads allow-top-navigation allow-top-navigation-by-user-activation"
                    src={iframeURL}
                    key={iframeURL}
                    onLoad={() => setIframeLoading(false)}
                    allowFullScreen
                    scrolling={false}
                  />
                ) : (
                  <div className="iframe-loader-skeleton" />
                )}
                <div className="bottom-arrows">
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                  <div
                    className={`flex-arrows nav_btn previous_btn ${isFirstUnit ? 'disabled' : ''}`}
                    onClick={!isFirstUnit ? handlePreviousUnit : undefined}
                  >
                    <div className="bottom_nav">
                      <svg
                        width={19}
                        height={18}
                        viewBox="0 0 19 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18.9849 10.296L1.87692 10.296L2.78692 9.568L11.1069 17.94L8.50692 17.94L0.00492268 9.36L8.53292 0.779999L11.1329 0.779999L2.78692 9.152L1.87692 8.424L18.9849 8.424L18.9849 10.296Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    {intl.formatMessage(messages.previous)}
                  </div>
                <div // eslint-disable-line
                  className="flex-arrows nav_btn next_btn"
                  style={{ display: (getNextCourseId(courseID || '', allowedCourses) === firstOfAllowedCourseId && isLastUnit) ? 'none' : 'flex' }}
                  onClick={!isLastUnit ? handleNextUnit : handleNextCourse}
                >
                  {intl.formatMessage(messages.saveAndContinue)}
                  <div className="bottom_nav ">
                    {' '}
                    <ArrowRightIcon />
                  </div>
                </div>
                </div>
              </div>
            </div>
          )}
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default CourseWorking;
